import {ButtonHTMLAttributes, forwardRef} from "react";
import {OtsGoBot, OtsGoBotState} from "./OtsGoBot";

export type OtsGoBotLauncherProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  /** Spoken name of what the button opens, e.g. "Send feedback". */
  label: string;
  /** Whether the panel it controls is showing; sets `aria-expanded`. */
  expanded?: boolean;
  state?: OtsGoBotState;
  /** Diameter in pixels. */
  size?: number;
};

/**
 * Round GoBot button for a floating corner or a nav bar. It only draws the button; the host decides where it sits
 * and what it opens.
 */
export const OtsGoBotLauncher = forwardRef<HTMLButtonElement, OtsGoBotLauncherProps>(function OtsGoBotLauncher({
  label,
  expanded,
  state = "idle",
  size = 48,
  className,
  type = "button",
  style,
  ...props
}, ref) {
  return <button
    ref={ref}
    type={type}
    aria-label={label}
    aria-expanded={expanded}
    title={label}
    style={{width: size, height: size, ...style}}
    className={[
      // `!overflow-hidden`: Mantine's unlayered `button {overflow: visible}` beats the Tailwind layer in DispatchGo/HubGo.
      "grid shrink-0 place-items-center rounded-full border !overflow-hidden",
      "border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] text-[var(--ots-secondary)]",
      "shadow-[0_8px_24px_-10px_rgb(15_23_42/0.45),0_1px_2px_rgb(15_23_42/0.08)] transition-transform",
      "hover:scale-105 active:scale-95 motion-reduce:transition-none motion-reduce:hover:scale-100",
      "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]",
      className,
    ].filter(Boolean).join(" ")}
    {...props}>
    <OtsGoBot state={state} size={Math.round(size * 0.62)}/>
  </button>;
});
